import {
  GridColumnVisibilityModel,
  GridFilterModel,
  GridSortModel,
} from '@mui/x-data-grid-premium';
import { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';
import { GridStateColDef } from '@mui/x-data-grid-pro/internals';
import { MutableRefObject } from 'react';

import { SavedReportDataGridSettingsConfig } from '~/data/reportSettings';

export const emptyDataGridSettings: Required<SavedReportDataGridSettingsConfig> =
  {
    columnVisibility: {},
    columnSorting: [],
    columnFiltering: { items: [] },
  };

export const getResetColumnVisibilityModel = (
  allColumns: GridStateColDef[],
): GridColumnVisibilityModel => {
  return Object.fromEntries(
    allColumns.map((c) => {
      return [c.field, true];
    }),
  );
};

const getColumnVisibilityModel = (
  apiRef: MutableRefObject<GridApiPremium>,
): GridColumnVisibilityModel => {
  if (!!apiRef.current.getAllColumns && !!apiRef.current.getVisibleColumns) {
    const allColumns = apiRef.current.getAllColumns();
    const columnVisibility = apiRef.current.getVisibleColumns();

    return Object.fromEntries(
      allColumns.map((c) => {
        return [
          c.field,
          !c.hideable || columnVisibility.some((vc) => vc.field === c.field),
        ];
      }),
    );
  }

  return emptyDataGridSettings.columnVisibility;
};

const getSortModel = (
  apiRef: MutableRefObject<GridApiPremium>,
): GridSortModel => {
  return !!apiRef.current.getSortModel
    ? apiRef.current.getSortModel()
    : emptyDataGridSettings.columnSorting;
};

const getFilterModel = (
  apiRef: MutableRefObject<GridApiPremium>,
): GridFilterModel => {
  return !!apiRef?.current?.state?.filter?.filterModel
    ? apiRef.current.state.filter.filterModel
    : emptyDataGridSettings.columnFiltering;
};

export const getDefaultDataGridSettings = (
  apiRef: MutableRefObject<GridApiPremium>,
): Required<SavedReportDataGridSettingsConfig> => {
  const { columnFiltering, columnSorting } = emptyDataGridSettings;

  return {
    columnVisibility: !!apiRef.current.getAllColumns
      ? getResetColumnVisibilityModel(apiRef.current.getAllColumns())
      : emptyDataGridSettings.columnVisibility,
    columnFiltering,
    columnSorting,
  };
};

export const resetDataGridSettings = (
  apiRef: MutableRefObject<GridApiPremium>,
) => {
  if (!!apiRef.current) {
    const { setColumnVisibilityModel, setSortModel, setFilterModel } =
      apiRef.current;

    const { columnVisibility, columnFiltering, columnSorting } =
      getDefaultDataGridSettings(apiRef);

    if (!!setColumnVisibilityModel) {
      setColumnVisibilityModel(columnVisibility);
    }
    if (!!setSortModel) {
      setSortModel(columnSorting);
    }
    if (!!setFilterModel) {
      setFilterModel(columnFiltering, 'restoreState');
    }
  }
};

export const getCurrentDataGridSettings = (
  apiRef: MutableRefObject<GridApiPremium>,
): SavedReportDataGridSettingsConfig => {
  return {
    columnVisibility: getColumnVisibilityModel(apiRef),
    columnSorting: getSortModel(apiRef),
    columnFiltering: getFilterModel(apiRef),
  };
};
